/*
 * Main entry for Materiaal Maatjes Vue components
 * - Registers components
 * - Exposes API on window.MateriaalMaatjes
 * - Auto-mounts components rendered via WP shortcode
 */

// Vue is provided as external (see webpack.config.js), but we import types/objects for bundlers
import { createApp, h } from 'vue';

// Global styles (optional). Webpack is configured with sass-loader and css-loader.
// If this causes issues you can comment it out, the WP enqueued CSS will still style components.
import '../css/global.scss';

// Components
import MmLogin from './components/MmLogin.vue';
import MmForgotPassword from './components/MmForgotPassword.vue';
import MmRegister from './components/MmRegister.vue';
import MmResetPassword from './components/MmResetPassword.vue';
import MmMaterialsOverview from './components/MmMaterialsOverview.vue';
import MmMaterialsDetail from './components/MmMaterialsDetail.vue';
import MmCategories from './components/MmCategories.vue';
import MmLatestMaterials from './components/MmLatestMaterials.vue';
import MmChangePassword from './components/MmChangePassword.vue';
import MmProfile from './components/MmProfile.vue';
import MmAccount from './components/MmAccount.vue';
import MmProductCreate from './components/MmProductCreate.vue';
import MmProductManage from './components/MmProductManage.vue';
import MmMyClaims from './components/MmMyClaims.vue';
import MmRegionClaims from './components/MmRegionClaims.vue';
import MmContact from './components/MmContact.vue';

// Component registry. Keys must match the value used in shortcode attribute `component="..."`
const registry = {
  MmLogin,
  MmForgotPassword,
  MmRegister,
  MmResetPassword,
  MmMaterialsOverview,
  MmMaterialsDetail,
  MmCategories,
  MmLatestMaterials,
  MmChangePassword,
  MmProfile,
  MmAccount,
  MmProductCreate,
  MmProductManage,
  MmMyClaims,
  MmRegionClaims,
  MmContact,
};

function parseProps(el) {
  const json = el.getAttribute('data-props');
  if (!json) return {};
  try {
    return JSON.parse(json);
  } catch (e) {
    console.warn('[MateriaalMaatjes] Invalid JSON in data-props:', e);
    return {};
  }
}

function normalizeName(name) {
  if (!name) return '';
  // Strip any HTML tags that may have been accidentally inserted (e.g., </span>),
  // then lowercase and strip non-alphanumerics to be lenient with kebab/case.
  const stripped = String(name).replace(/<[^>]*>/g, '').trim();
  return stripped.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function resolveComponent(name) {
  const norm = normalizeName(name);
  // Try exact key first
  if (registry[name]) return registry[name];
  // Then try normalized match among registered keys
  const key = Object.keys(registry).find(k => normalizeName(k) === norm);
  return key ? registry[key] : undefined;
}

// Keep a weak map of mounted instances to avoid double mounting and allow GC
const mountedMap = new WeakMap();

function mountElement(el) {
  if (!el || !(el instanceof Element)) return null;
  // Guard: do not double-mount the same element
  if (el.__mmMounted || el.getAttribute('data-mm-mounted') === 'true' || mountedMap.has(el)) {
    return null;
  }
  const name = el.getAttribute('data-component');
  const Comp = resolveComponent(name);
  if (!Comp) {
    console.warn(`[MateriaalMaatjes] Component not found: ${name}`);
    return null;
  }

  const props = parseProps(el);
  // Merge global defaults from localized WP settings (materiaalAjax)
  // Ensure we pass the login status to all components that support it
  const globalDefaults = {};
  try {
    if (typeof window !== 'undefined' && window.materiaalAjax) {
      if (typeof window.materiaalAjax.isLoggedIn !== 'undefined') {
        globalDefaults.isLoggedIn = !!window.materiaalAjax.isLoggedIn;
      }
    }
    // Also merge from materiaalMaatjesData if available
    if (typeof window !== 'undefined' && window.materiaalMaatjesData) {
      if (window.materiaalMaatjesData.user) {
        globalDefaults.currentUser = window.materiaalMaatjesData.user;
      }
    }
  } catch (e) {
    // noop: safe guard if window or materiaalAjax is not accessible
  }
  const mergedProps = Object.assign({}, globalDefaults, props);
  const app = createApp({
    data() {
      return { current: Comp };
    },
    render() {
      const comp = this.current;
      const handlers = {};
      // Enable simple in-place navigation between Login, Register and Forgot Password without a router
      if (registry.MmLogin) {
        if (registry.MmForgotPassword) {
          handlers.onForgotPassword = () => { this.current = registry.MmForgotPassword; };
        }
        if (registry.MmRegister) {
          handlers.onRequestSignup = () => { this.current = registry.MmRegister; };
        }
        if (registry.MmChangePassword) {
          handlers.onRequestChangePassword = () => { this.current = registry.MmChangePassword; };
        }
        handlers.onBackToLogin = () => { this.current = registry.MmLogin; };
        // Generic navigate hook to switch to any registered component by name
        handlers.onNavigate = (name) => {
          if (registry[name]) this.current = registry[name];
        };
      }
      return h(comp, Object.assign({}, mergedProps, handlers));
    },
  });

  // Provide a way to unmount later
  const vm = app.mount(el);
  const instance = { app, vm, name };
  // Mark element as mounted to prevent duplicate mounts
  try { el.__mmMounted = true; el.setAttribute('data-mm-mounted', 'true'); } catch(_) {}
  mountedMap.set(el, instance);
  // Track globally for debugging/support
  if (typeof window !== 'undefined') {
    const w = window;
    w.MateriaalMaatjes = w.MateriaalMaatjes || {};
    w.MateriaalMaatjes.instances = w.MateriaalMaatjes.instances || [];
    w.MateriaalMaatjes.instances.push({ el, ...instance });
  }
  return instance;
}

function autoMount(root = document) {
  if (!root || !root.querySelectorAll) return [];
  const nodes = root.querySelectorAll('[data-component]');
  const instances = [];
  nodes.forEach((el) => {
    const mounted = mountElement(el);
    if (mounted) instances.push(mounted);
  });
  return instances;
}

// Expose a small public API
const api = {
  components: registry,
  mount: mountElement,
  mountAll: autoMount,
};

// Attach to window for WordPress usage (as defined by webpack libraryTarget: 'window')
// Avoid overriding if already defined (HMR/duplicate includes)
window.MateriaalMaatjes = Object.assign(window.MateriaalMaatjes || {}, api);

// Auto-mount when DOM is ready
// Initial auto-mounts and resilience against late DOM mutations
const runInitialMounts = () => { try { autoMount(); } catch(_) {} };
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runInitialMounts, { once: true });
} else {
  runInitialMounts();
}
// Also run on full window load for themes/builders that inject after DOMContentLoaded
window.addEventListener('load', () => { setTimeout(runInitialMounts, 0); setTimeout(runInitialMounts, 800); }, { once: true });

// Observe DOM changes to mount components that appear later (e.g., page builders)
try {
  const observer = new MutationObserver((mutations) => {
    let needsScan = false;
    for (const m of mutations) {
      if (m.type === 'childList') {
        if (m.addedNodes && m.addedNodes.length) {
          // Quick path: directly mount on any added element(s) that have data-component
          m.addedNodes.forEach((n) => {
            if (!(n instanceof Element)) return;
            if (n.hasAttribute && n.hasAttribute('data-component')) {
              mountElement(n);
            }
            // If subtree contains components, schedule a scan (cheaper than deep query per node)
            if (!needsScan && n.querySelector && n.querySelector('[data-component]')) needsScan = true;
          });
        }
      }
    }
    if (needsScan) {
      // Debounce a micro scan to mount any missed nodes in the affected subtree
      clearTimeout(observer.__mmDebounce);
      observer.__mmDebounce = setTimeout(() => autoMount(document), 50);
    }
  });
  observer.observe(document.documentElement || document.body, { childList: true, subtree: true });
} catch (e) {
  // MutationObserver may not be available in very old browsers; ignore.
}
